import { Drawer, CartDrawer, fetchVariantMetafieldsViaSection, ProductForm } from 'theme';

class BundleVariantCard extends HTMLElement {
  constructor() {
    super();
    this._abortController = new AbortController();
    this._initialized = false;
    this._minQuantity = 1;
    this._suspendQuantityEvents = false;
    this._onCheckboxChange = this._onCheckboxChange.bind(this);
    this._onQuantityChange = this._onQuantityChange.bind(this);
    this._onQuantityControlClick = this._onQuantityControlClick.bind(this);
  }

  connectedCallback() {
    // Always create a fresh AbortController so listeners survive disconnect/reconnect
    // (e.g. when a Drawer moves this element to document.body on open)
    this._abortController = new AbortController();

    if (!this._initialized) {
      this._initialized = true;

      this._checkbox = this.querySelector('input[type="checkbox"][name="id"]');
      this._stack = this.querySelector('layered-button-stack-lite');
      this._quantitySelector = this.querySelector('quantity-selector');
      this._quantityInput = this._quantitySelector?.querySelector('input');

      if (!this._checkbox || !this._stack || !this._quantitySelector || !this._quantityInput) {
        return;
      }

      const quantityButtons = this._quantitySelector.querySelectorAll('button');
      this._decrementButton = quantityButtons[0] || null;
      this._incrementButton = quantityButtons[1] || null;

      const parsedMin = parseInt(this._quantityInput.getAttribute('min') || '1', 10);
      this._minQuantity = Number.isFinite(parsedMin) && parsedMin > 0 ? parsedMin : 1;
    }

    if (!this._checkbox || !this._stack || !this._quantitySelector || !this._quantityInput) {
      return;
    }

    // (Re-)attach listeners with the fresh AbortController signal
    this._quantitySelector.addEventListener('click', this._onQuantityControlClick, {
      signal: this._abortController.signal,
      capture: true,
    });
    this._checkbox.addEventListener('change', this._onCheckboxChange, {
      signal: this._abortController.signal,
    });
    this._quantityInput.addEventListener('change', this._onQuantityChange, {
      signal: this._abortController.signal,
    });
    this._quantityInput.addEventListener('input', this._onQuantityChange, {
      signal: this._abortController.signal,
    });

    this._setQuantity(this._quantityInput.value || this._minQuantity, { silent: true });

    if (this._checkbox.checked) {
      this._activateQuantity({ focus: false });
    } else {
      this._deactivateQuantity({ resetQuantity: true });
    }
  }

  disconnectedCallback() {
    this._abortController.abort();
  }

  _onCheckboxChange(event) {
    if (event.target.checked) {
      this._activateQuantity({ focus: false });
    } else {
      this._deactivateQuantity({ resetQuantity: true });
    }
  }

  _onQuantityChange() {
    if (this._suspendQuantityEvents) return;

    this._syncQuantityAttr();

    const currentQty = this._getQuantity();
    if (!this._checkbox.checked && currentQty >= this._minQuantity) {
      this._checkbox.checked = true;
      this._activateQuantity({ focus: false });
      return;
    }

    if (currentQty < this._minQuantity && this._checkbox.checked) {
      this._checkbox.checked = false;
      this._deactivateQuantity({ resetQuantity: true });
    }
  }

  _onQuantityControlClick(event) {
    const button = event.target.closest('button');
    if (!button) return;

    if (button === this._decrementButton) {
      const currentQty = this._getQuantity();
      if (currentQty <= this._minQuantity) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this._checkbox.checked) {
          this._checkbox.checked = false;
          this._deactivateQuantity({ resetQuantity: true });
        }
        return;
      }
    }

    if (button === this._incrementButton && !this._checkbox.checked) {
      this._checkbox.checked = true;
      this._activateQuantity({ focus: false });
    }
  }

  _activateQuantity({ focus = true } = {}) {
    this._showStackPanel(1);
    this._quantityInput.disabled = false;
    this.classList.add('bundle-variant-card--active');
    this._syncQuantityAttr();
    if (focus) {
      requestAnimationFrame(() => this._quantityInput.focus());
    }
  }

  _deactivateQuantity({ resetQuantity = false } = {}) {
    this._showStackPanel(0);
    this.classList.remove('bundle-variant-card--active');
    this._quantityInput.disabled = true;
    if (resetQuantity) {
      this._setQuantity(this._minQuantity);
    }
    this._syncQuantityAttr();
  }

  _showStackPanel(index) {
    if (!this._stack) return;
    if (typeof this._stack.showButton === 'function') {
      this._stack.showButton(index);
    } else {
      this._stack.setAttribute('active-index', String(index));
    }
  }

  _getQuantity() {
    const value = parseInt(this._quantityInput.value, 10);
    return Number.isFinite(value) ? value : 0;
  }

  _setQuantity(value, { silent = false } = {}) {
    const safeValue = Math.max(this._minQuantity, parseInt(value, 10) || this._minQuantity);
    this._quantityInput.value = safeValue;
    this._syncQuantityAttr(safeValue);
    if (!silent) {
      this._suspendQuantityEvents = true;
      this._quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
      this._quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      this._suspendQuantityEvents = false;
    }
  }

  _syncQuantityAttr(nextValue) {
    if (!this._checkbox) return;
    const value = Number.isFinite(nextValue)
      ? nextValue
      : Math.max(this._minQuantity, parseInt(this._quantityInput.value, 10) || this._minQuantity);
    this._checkbox.dataset.productQty = String(value);
  }
}

if (!window.customElements.get('bundle-variant-card')) {
  window.customElements.define('bundle-variant-card', BundleVariantCard);
}

class BundlePicker extends HTMLElement {
  constructor() {
    super();
    this._abortController = new AbortController();
    this._form = null;
    this._progressRewardsThreshold = this.getAttribute('data-progress-rewards-threshold') || 4;
    // Support both the legacy <progress-bar> and the new <variant-bundle-progress>
    this._progressBar = this.querySelector('variant-bundle-progress, progress-bar');
    this._sellingPlans = [];
  }

  connectedCallback() {
    // Always create a fresh AbortController so listeners survive disconnect/reconnect
    // (e.g. when a Drawer moves this element to document.body on open)
    this._abortController = new AbortController();

    // Re-query progress bar in case DOM was rebuilt
    if (!this._progressBar) {
      this._progressBar = this.querySelector('variant-bundle-progress, progress-bar');
    }

    const formId = this.getAttribute('form');
    if (formId) {
      // Ensure we get a single form element, not a collection
      const formById = document.getElementById(formId);
      let formByName = null;
      
      // document.forms[name] can return a RadioNodeList for radio buttons, so we must check for HTMLFormElement
      if (document.forms[formId]) {
        const formCandidate = document.forms[formId];
        if (formCandidate instanceof HTMLFormElement) {
          formByName = formCandidate;
        }
      }
      
      // Only set _form if it's actually a form element
      this._form = (formById instanceof HTMLFormElement ? formById : null) ||
                   (formByName instanceof HTMLFormElement ? formByName : null);
    }

    this._loadSellingPlans();

    this.addEventListener('change', this._onSelectionChange.bind(this), {
      signal: this._abortController.signal,
      capture: true,
    });

    this.addEventListener('input', this._onSelectionChange.bind(this), {
      signal: this._abortController.signal,
      capture: true,
    });

    // Listen to bundle:selection-change events to update selling plan
    this.addEventListener('bundle:selection-change', this._onBundleSelectionChange.bind(this), {
      signal: this._abortController.signal,
    });

    // Listen for selling plan changes to update subscription info
    if (this._form) {
      this._form.addEventListener('change', (event) => {
        if (event.target.name === 'selling_plan' && event.target.hasAttribute('data-selling-plan-input')) {
          const selectedCards = this.querySelectorAll('bundle-variant-card input[name="id"]:checked');
          const selectedItems = Array.from(selectedCards).map((input) => {
            const variantCard = input.closest('bundle-variant-card');
            const quantityInput = variantCard?.querySelector('input[type="number"][is="quantity-input"]');
            const quantity = quantityInput ? parseInt(quantityInput.value || '1', 10) : parseInt(input.dataset.productQty || '1', 10);
            return quantity;
          });
          const totalQuantity = selectedItems.reduce((sum, qty) => sum + qty, 0);
          this._updateSubscriptionInfo(totalQuantity);
        }
      }, {
        signal: this._abortController.signal,
      });
    }

    this._updateFormState();
  }

  disconnectedCallback() {
    this._abortController.abort();
  }

  _onSelectionChange(event) {
    if (event.target.type === 'checkbox' && event.target.name === 'id') {
      this._updateFormState();
    } else if (event.target.type === 'number' && event.target.hasAttribute('is') && event.target.getAttribute('is') === 'quantity-input') {
      this._updateFormState();
    }
  }

  _calculateProgress(selectedCount = 0) {
    if (!this._progressBar) return;
    const progressSteps = this.querySelectorAll('.progress-bar-wrapper__step');
    const progressWrapper = this._progressBar.closest('.progress-bar-wrapper');
    const hasMedals = progressWrapper?.classList.contains('progress-bar-wrapper--with-medals');
    const totalSteps = progressSteps.length;
    
    let progress;
    if (selectedCount === 0) {
      progress = 0;
    } else if (selectedCount === 1) {
      // First step achieved should show 0% progress
      progress = 0;
    } else if (hasMedals && totalSteps > 1) {
      // For progress bars with medals, stop exactly at each medal
      // Progress goes from medal 1 (0%) to medal N (100%)
      progress = 100 * (selectedCount - 1) / (totalSteps - 1);
    } else {
      // For regular progress bars, use standard calculation
      progress = (selectedCount / this._progressRewardsThreshold) * 100;
    }
    
    progressSteps.forEach((step, index) => {
      if (index < selectedCount) {
        step.setAttribute('active', '');
      } else {
        step.removeAttribute('active');
      }
      // Remove the active-last class from all steps first
      step.classList.remove('progress-bar-wrapper__step--active-last');
    });

    this._progressBar.style.setProperty('--progress', `${progress}%`);
    this._progressBar.setAttribute('aria-valuenow', String(progress));
    // Add active-last class to the last active step (if any)
    if (selectedCount > 0) {
      const lastActiveIndex = Math.min(selectedCount - 1, progressSteps.length - 1);
      if (progressSteps[lastActiveIndex]) {
        progressSteps[lastActiveIndex].classList.add('progress-bar-wrapper__step--active-last');
      }
    }
  }

  _loadSellingPlans() {
    // Try to find the script in the form first, then globally
    let sellingPlansScript = this._form?.querySelector('#form-bundle-selling-plans') || document.getElementById('form-bundle-selling-plans');
    if (!sellingPlansScript) {
      console.log('[BundlePicker] Selling plans script not found');
      this._sellingPlans = [];
      return;
    }
    try {
      const raw = sellingPlansScript.textContent?.trim() || '';
      // The Liquid template provides a proper JSON array: [{...}, {...}]
      // SEPA plans are already filtered out in Liquid
      const parsed = JSON.parse(raw);
      
      // The Liquid template ensures it's always an array
      this._sellingPlans = Array.isArray(parsed) ? parsed : [];
      
      console.log('[BundlePicker] Loaded selling plans:', this._sellingPlans.length);
      if (this._sellingPlans.length > 0) {
        console.log('[BundlePicker] Selling plan IDs:', this._sellingPlans.map(p => ({ id: p.id, name: p.name })));
      }
    } catch (e) {
      console.error('[BundlePicker] Error loading selling plans:', e);
      console.error('[BundlePicker] Raw content preview:', sellingPlansScript.textContent?.substring(0, 200));
      this._sellingPlans = [];
    }
  }

  _onBundleSelectionChange(event) {
    // Update selling plan based on total quantity
    const detail = event.detail || {};
    const selectedItems = detail.selectedItems || [];
    const totalQuantity = selectedItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
    
    console.log('[BundlePicker] Selection changed - totalQuantity:', totalQuantity, 'selectedItems:', selectedItems);
    this._updateSellingPlanBasedOnQuantity(totalQuantity);
  }

  _updateSellingPlanBasedOnQuantity(totalQuantity) {
    console.log('[BundlePicker] _updateSellingPlanBasedOnQuantity called with quantity:', totalQuantity);
    
    if (!this._form) {
      console.log('[BundlePicker] No form found');
      return;
    }

    // Reload selling plans if not loaded yet
    if (this._sellingPlans.length === 0) {
      console.log('[BundlePicker] Reloading selling plans...');
      this._loadSellingPlans();
    }

    if (this._sellingPlans.length === 0) {
      console.log('[BundlePicker] No selling plans available');
      return;
    }

    // Calculate which plan index to use (quantity - 1, capped at last available plan)
    // Each added item upgrades to the next available plan
    const planIndex = Math.min(totalQuantity - 1, this._sellingPlans.length - 1);
    console.log('[BundlePicker] Calculated planIndex:', planIndex, 'from quantity:', totalQuantity, 'available plans:', this._sellingPlans.length);
    
    // If quantity is 0, don't change the plan but still update subscription info
    if (totalQuantity <= 0) {
      console.log('[BundlePicker] Quantity is 0, skipping plan update');
      this._updateSubscriptionInfo(totalQuantity, null);
      return;
    }

    // Get the selling plan ID at the calculated index
    const targetPlan = this._sellingPlans[planIndex];
    if (!targetPlan || !targetPlan.id) {
      console.log('[BundlePicker] No target plan found at index:', planIndex);
      return;
    }

    const targetPlanId = String(targetPlan.id);
    
    // Extract frequency as integer from plan name (e.g., "Every 2 months" -> 2)
    let frequency = null;
    if (targetPlan.name) {
      const match = targetPlan.name.match(/(\d+)\s*months?/i);
      if (match && match[1]) {
        frequency = parseInt(match[1], 10);
      }
    }
    
    console.log('[BundlePicker] Target plan ID:', targetPlanId, 'plan name:', targetPlan.name, 'frequency:', frequency, 'months');

    // Find the subscription plan input (the one with data-selling-plan-input attribute)
    // In bundle-form.liquid, only ONE subscription radio button is rendered, and we need to update its value
    const subscriptionInput = this._form.querySelector('input[data-selling-plan-input]');
    
    if (!subscriptionInput) {
      console.log('[BundlePicker] No subscription input found with data-selling-plan-input');
      return;
    }

    console.log('[BundlePicker] Found subscription input, current value:', subscriptionInput.value, 'target value:', targetPlanId);

    // Get current plan frequency if available
    let currentFrequency = null;
    if (subscriptionInput.value) {
      const currentPlan = this._sellingPlans.find(p => String(p.id) === subscriptionInput.value);
      if (currentPlan && currentPlan.name) {
        const match = currentPlan.name.match(/(\d+)\s*months?/i);
        if (match && match[1]) {
          currentFrequency = parseInt(match[1], 10);
        }
      }
    }
    console.log('[BundlePicker] Current frequency:', currentFrequency, 'months, Target frequency:', frequency, 'months');

    // Only update if the plan is different from current selection
    if (subscriptionInput.value === targetPlanId && subscriptionInput.checked) {
      console.log('[BundlePicker] Plan already selected, skipping update. Frequency:', frequency, 'months');
      // Still update subscription info in case quantity changed
      this._updateSubscriptionInfo(totalQuantity, frequency);
      return;
    }

    // Update the value of the subscription input to match the target plan ID
    subscriptionInput.value = targetPlanId;
    console.log('[BundlePicker] Updated subscription input value to:', targetPlanId);

    // IMPORTANT: Never automatically switch the user from one-time to subscription.
    // Only update which selling plan is pre-selected on the subscription radio,
    // so that if/when the user switches to subscription, the correct plan is ready.
    const oneTimeInput = this._form.querySelector('input[name="selling_plan"][value=""]');
    const isOneTimeSelected = oneTimeInput && oneTimeInput.checked;

    if (isOneTimeSelected) {
      // User explicitly chose one-time purchase — respect that choice.
      // We've already updated subscriptionInput.value above so the right plan
      // is ready if the user decides to switch to subscription later.
      console.log('[BundlePicker] One-time purchase is selected, not switching to subscription. Updated subscription plan value to:', targetPlanId);
      this._updateSubscriptionInfo(totalQuantity, frequency);
      return;
    }

    // Subscription is already selected — ensure the radio is checked and fire change
    subscriptionInput.checked = true;
    console.log('[BundlePicker] Checked subscription input');
    
    // Trigger change event to notify other components (like SellingPlanPicker)
    subscriptionInput.dispatchEvent(new Event('change', { bubbles: true }));
    console.log('[BundlePicker] Dispatched change event, selected frequency:', frequency, 'months');
    
    // Update subscription info text
    this._updateSubscriptionInfo(totalQuantity, frequency);
  }

  _updateSubscriptionInfo(totalQuantity = 0, frequency = null) {
    if (!this._form) return;
    
    const subscriptionInfoElement = this._form.querySelector('[data-subscription-info]');
    if (!subscriptionInfoElement) {
      console.log('[BundlePicker] Subscription info element not found');
      return;
    }

    // Get frequency from current selling plan if not provided
    if (frequency === null) {
      const subscriptionInput = this._form.querySelector('input[data-selling-plan-input]');
      if (subscriptionInput && subscriptionInput.value) {
        const currentPlan = this._sellingPlans.find(p => String(p.id) === subscriptionInput.value);
        if (currentPlan && currentPlan.name) {
          const match = currentPlan.name.match(/(\d+)\s*months?/i);
          if (match && match[1]) {
            frequency = parseInt(match[1], 10);
          }
        }
      }
    }

    // Get strings from themeVariables
    const strings = window.themeVariables?.strings;
    if (!strings) {
      console.log('[BundlePicker] Theme variables not available');
      return;
    }

    let message = '';
    
    if (totalQuantity === 0) {
      // No products selected
      message = strings.noProductsSelectedHtml || '';
    } else if (totalQuantity === 1) {
      // One product selected
      message = strings.oneProductSelectedHtml || '';
    } else {
      // Multiple products selected
      message = strings.multipleProductsSelectedHtml || '';
    }

    // Replace placeholders
    if (frequency !== null && frequency !== undefined) {
      message = message.replace(/__FREQUENCY__/g, String(frequency));
    } else {
      // If no frequency, remove frequency-related content
      message = message.replace(/every\s+<span[^>]*data-frequency[^>]*>.*?<\/span>\s+months?/gi, '');
      message = message.replace(/__FREQUENCY__/g, '');
    }

    if (totalQuantity > 1) {
      message = message.replace(/__COUNT__/g, String(totalQuantity));
    }

    // Update the element's innerHTML
    subscriptionInfoElement.innerHTML = message;
    
    // Update any spans that might need updating (after innerHTML is set)
    if (frequency !== null && frequency !== undefined) {
      const frequencySpans = subscriptionInfoElement.querySelectorAll('[data-frequency]');
      frequencySpans.forEach(span => {
        span.textContent = String(frequency);
      });
    }
    
    if (totalQuantity > 1) {
      const qtySpans = subscriptionInfoElement.querySelectorAll('[data-total-qty]');
      qtySpans.forEach(span => {
        span.textContent = String(totalQuantity);
      });
    }
  }

  _updateFormState() {
    const selectedCards = this.querySelectorAll('bundle-variant-card input[name="id"]:checked');
    const selectedCount = selectedCards.length;
    const selectedItems = Array.from(selectedCards).map((input) => {
      const variantCard = input.closest('bundle-variant-card');
      const quantityInput = variantCard?.querySelector('input[type="number"][is="quantity-input"]');
      const quantity = quantityInput ? parseInt(quantityInput.value || '1', 10) : parseInt(input.dataset.productQty || '1', 10);

      return {
        variantId: input.value,
        quantity: quantity,
        price: parseFloat(input.dataset.price || '0', 10),
      };
    });
    
    const totalQuantity = selectedItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
    this._calculateProgress(totalQuantity);
    
    // Update selling plan based on quantity (call directly instead of relying on event listener)
    // This will also update subscription info
    this._updateSellingPlanBasedOnQuantity(totalQuantity);
    
    // Also update subscription info directly in case selling plan update was skipped (e.g., quantity is 0)
    this._updateSubscriptionInfo(totalQuantity);
    
    this.dispatchEvent(
      new CustomEvent('bundle:selection-change', {
        bubbles: true,
        detail: {
          selectedCount,
          selectedItems,
        },
      })
    );

    if (this._form) {
      const hasSelection = selectedCount > 0;
      const submitButton = this._form.querySelector('button[type="submit"]');
      if (submitButton) {
        submitButton.disabled = !hasSelection;
      }
    }
  }

  reset() {
    this._resetFormState();
  }

  _resetFormState() {
    this.querySelectorAll('bundle-variant-card input[name="id"]:checked').forEach((input) => {
      input.checked = false;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    this.querySelectorAll('bundle-variant-card').forEach((card) => {
      if (typeof card._deactivateQuantity === 'function') {
        card._deactivateQuantity({ resetQuantity: true });
      }
    });

    this._updateFormState();
  }

  getSelectedItems() {
    const checkboxes = this.querySelectorAll('input[name="id"]:checked');
    return Array.from(checkboxes).map((input) => {
      const variantCard = input.closest('bundle-variant-card');
      const quantityInput = variantCard?.querySelector('input[type="number"][is="quantity-input"]');
      const quantity = quantityInput ? parseInt(quantityInput.value || '1', 10) : parseInt(input.dataset.productQty || '1', 10);

      return {
        id: parseInt(input.value, 10),
        quantity: quantity,
        price: parseFloat(input.dataset.price || '0', 10),
      };
    });
  }
}

if (!window.customElements.get('bundle-picker')) {
  window.customElements.define('bundle-picker', BundlePicker);
}

/**
 * Minimal progress component for bundle pages.
 * - Reads base prices from descendants with [data-step-price] (value in cents).
 * - If a subscription selling plan is selected on the related form,
 *   applies the selling plan percentage discount to each step price.
 * - If no subscription is selected, leaves prices as-is (renders base values).
 *
 * Usage:
 * <variant-bundle-progress form="{{ form_id }}">
 *   ... any markup ...
 *   <span data-step-price="2200"></span>
 * </variant-bundle-progress>
 */
class VariantBundleProgress extends HTMLElement {
  constructor() {
    super();
    this._abortController = new AbortController();
    this._form = null;
    this._sellingPlans = [];
    this._currencyFormatter = new Intl.NumberFormat(Shopify.locale, {
      style: 'currency',
      currency: Shopify.currency.active,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }

  connectedCallback() {
    // Always create a fresh AbortController so listeners survive disconnect/reconnect
    this._abortController = new AbortController();

    const formId = this.getAttribute('form');
    // Resolve related form robustly - ensure we get a single form element, not a collection
    const formById = formId ? document.getElementById(formId) : null;
    let formByName = null;
    
    // document.forms[name] can return a RadioNodeList for radio buttons, so we must check for HTMLFormElement
    if (formId && document.forms[formId]) {
      const formCandidate = document.forms[formId];
      if (formCandidate instanceof HTMLFormElement) {
        formByName = formCandidate;
      }
    }
    
    const formByClosest = this.closest('form[is="product-form-bundle"]') || 
                         this.closest('product-form-bundle') || 
                         this.closest('form');
    
    // Only set _form if it's actually a form element (formByClosest might be a custom element, not a form)
    this._form = (formById instanceof HTMLFormElement ? formById : null) ||
                 (formByName instanceof HTMLFormElement ? formByName : null) ||
                 (formByClosest instanceof HTMLFormElement ? formByClosest : null);

    this._loadSellingPlans();

    // React to selling plan changes and bundle changes
    const updateHandler = this._updateStepPrices.bind(this);
    if (this._form) {
      // Attach directly on radios present now
      this._form.querySelectorAll('input[name="selling_plan"]').forEach((input) => {
        input.addEventListener('change', updateHandler, { signal: this._abortController.signal });
      });
      // Listen for custom bundle events from the form
      this._form.addEventListener('bundle:selection-change', updateHandler, {
        signal: this._abortController.signal,
      });
    }
    // Also listen at document level in case radios are rendered later or outside direct subtree
    document.addEventListener(
      'change',
      (ev) => {
        const target = ev.target;
        if (!(target instanceof HTMLInputElement)) return;
        if (target.name !== 'selling_plan') return;
        const targetForm = target.form || target.closest('form');
        if (this._form && targetForm !== this._form) return;
        this._updateStepPrices();
      },
      { signal: this._abortController.signal, capture: true }
    );
    // Listen for bundle events globally and filter to our form
    document.addEventListener(
      'bundle:selection-change',
      (ev) => {
        if (!this._form) {
          this._updateStepPrices();
          return;
        }
        
        // Ensure _form is an actual form element with a valid string id
        const formId = this._form instanceof HTMLFormElement && typeof this._form.id === 'string' ? this._form.id : null;
        const isSameForm =
          ev.target === this._form ||
          (ev.target instanceof Element && formId && typeof formId === 'string' && !!ev.target.closest(`#${formId}`));
        
        if (isSameForm) {
          this._updateStepPrices();
        }
      },
      { signal: this._abortController.signal }
    );

    this._updateStepPrices();
  }

  disconnectedCallback() {
    this._abortController.abort();
  }

  _loadSellingPlans() {
    // Try to find the script in the form first, then globally
    let sellingPlansScript = this._form?.querySelector('#form-bundle-selling-plans') || document.getElementById('form-bundle-selling-plans');
    if (!sellingPlansScript) {
      this._sellingPlans = [];
      return;
    }
    try {
      const raw = sellingPlansScript.textContent?.trim() || '';
      let parsed;
      if (raw.startsWith('[')) {
        parsed = JSON.parse(raw);
      } else {
        parsed = JSON.parse('[' + raw + ']');
      }
      this._sellingPlans = Array.isArray(parsed) ? parsed : [parsed];
    } catch (_) {
      this._sellingPlans = [];
    }
  }

  _getSelectedSellingPlanId() {
    if (!this._form) return null;
    const input = this._form.querySelector('input[name="selling_plan"]:checked');
    const val = input?.value || null;
    return val && val !== '' ? val : null;
  }

  _getSellingPlanDiscountPercent(sellingPlanId) {
    if (!sellingPlanId) return 0;
    const plan = this._sellingPlans.find((p) => p?.id === parseInt(sellingPlanId, 10));
    if (!plan || !Array.isArray(plan.price_adjustments) || plan.price_adjustments.length === 0) return 0;
    const adj = plan.price_adjustments[0];
    if (adj?.value_type === 'percentage') {
      const v = parseFloat(adj.value);
      return Number.isFinite(v) ? v : 0;
    }
    return 0;
  }

  _updateStepPrices() {
    const container = this.closest('.progress-bar-wrapper') || this.parentElement || this;
    const stepPriceNodes = container.querySelectorAll('[data-step-price]');
    if (!stepPriceNodes.length) return;

    const sellingPlanId = this._getSelectedSellingPlanId();
    if (!sellingPlanId) {
      // No subscription selected => leave prices as base values
      stepPriceNodes.forEach((el) => {
        const baseCents = parseInt(el.getAttribute('data-step-price') || '0', 10) || 0;
        if (!el.hasAttribute('data-original-text')) {
          // If consumer had custom text, preserve it as original for potential restores.
          el.setAttribute('data-original-text', el.textContent || '');
        }
        el.textContent = this._currencyFormatter.format(baseCents / 100);
      });
      return;
    }

    // Apply selling plan discount percentage and format
    const discountPercent = this._getSellingPlanDiscountPercent(sellingPlanId);
    const discountMultiplier = (100 - discountPercent) / 100;
    stepPriceNodes.forEach((el) => {
      const baseCents = parseInt(el.getAttribute('data-step-price') || '0', 10) || 0;
      let finalCents = Math.round(baseCents * discountMultiplier);
      // Round to whole euro
      finalCents = Math.round(finalCents / 100) * 100;
      el.textContent = this._currencyFormatter.format(finalCents / 100);
    });
  }
}

if (!window.customElements.get('variant-bundle-progress')) {
  window.customElements.define('variant-bundle-progress', VariantBundleProgress);
}

class BundleBuyButtons extends HTMLElement {
  constructor() {
    super();
    this._abortController = new AbortController();
    this._currencyFormatter = new Intl.NumberFormat(Shopify.locale, {
      style: 'currency',
      currency: Shopify.currency.active,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }

  connectedCallback() {
    // Always create a fresh AbortController so listeners survive disconnect/reconnect
    this._abortController = new AbortController();

    this._buyButtons = this;
    this._onCartErrorListener = this._onCartError.bind(this);
    const formId = this.getAttribute('form');
    
    if (formId) {
      // Try multiple methods to find the form, similar to VariantBundleProgress
      const formById = document.getElementById(formId);
      let formByName = null;
      
      // document.forms[name] can return a RadioNodeList for radio buttons, so we must check for HTMLFormElement
      if (document.forms[formId]) {
        const formCandidate = document.forms[formId];
        if (formCandidate instanceof HTMLFormElement) {
          formByName = formCandidate;
        }
      }
      
      // Also try to find custom element product-form-bundle
      const formByCustomElement = document.querySelector(`form[is="product-form-bundle"]#${formId}`) ||
                                   document.querySelector(`product-form-bundle#${formId}`) ||
                                   document.querySelector(`form#${formId}[is="product-form-bundle"]`);
      
      // Try closest form as fallback
      const formByClosest = this.closest('form[is="product-form-bundle"]') || 
                           this.closest('product-form-bundle') || 
                           this.closest('form');
      
      // Accept HTMLFormElement or custom element (product-form-bundle)
      this._productForm = formById ||
                         formByName ||
                         formByCustomElement ||
                         (formByClosest instanceof HTMLFormElement ? formByClosest : formByClosest);
      
      this._productForm?.addEventListener('cart:error', this._onCartErrorListener);
    }

    this._button = this.querySelector('button[type="submit"]');

    if (this._button) {
      const hasContentElement = this._button.contentElement && typeof this._button.contentElement === 'object';
      const targetElement = hasContentElement ? this._button.contentElement : this._button;
      this._qtySpan = targetElement.querySelector('[data-total-qty]');
      this._priceSpan = targetElement.querySelector('[data-total-price]');
      this._nonSubscriptionPriceSpan = targetElement.querySelector('[data-non-subscription-price]');
    } else {
      this._qtySpan = this.querySelector('[data-total-qty]');
      this._priceSpan = this.querySelector('[data-total-price]');
      this._nonSubscriptionPriceSpan = this.querySelector('[data-non-subscription-price]');
    }

    if (formId && this._productForm) {
      this._bundlePicker = this._productForm.querySelector('bundle-picker');
    }

    document.addEventListener('bundle:selection-change', this._onSelectionChange.bind(this), {
      signal: this._abortController.signal,
    });

    // If form wasn't found initially, try again after a short delay (for dynamically rendered forms)
    if (formId && !this._productForm) {
      setTimeout(() => {
        this._retryFormLookup(formId);
        // Update button after form is found
        if (this._productForm) {
          this._updateButton();
        }
      }, 100);
    }

    this._updateButton();
  }

  _retryFormLookup(formId) {
    if (!formId) return;
    
    // Try all the same methods again
    const formById = document.getElementById(formId);
    let formByName = null;
    
    if (document.forms[formId]) {
      const formCandidate = document.forms[formId];
      if (formCandidate instanceof HTMLFormElement) {
        formByName = formCandidate;
      }
    }
    
    const formByCustomElement = document.querySelector(`form[is="product-form-bundle"]#${formId}`) ||
                                 document.querySelector(`product-form-bundle#${formId}`) ||
                                 document.querySelector(`form#${formId}[is="product-form-bundle"]`);
    
    const formByClosest = this.closest('form[is="product-form-bundle"]') || 
                         this.closest('product-form-bundle') || 
                         this.closest('form');
    
    // Also try searching within parent elements (for cases where form is in a drawer/modal)
    const parentQuickAdd = this.closest('product-quick-add');
    let formInParent = null;
    if (parentQuickAdd) {
      formInParent = parentQuickAdd.querySelector(`form#${formId}`) ||
                     parentQuickAdd.querySelector(`form[is="product-form-bundle"]#${formId}`) ||
                     parentQuickAdd.querySelector(`product-form-bundle#${formId}`);
    }
    
    this._productForm = formById ||
                       formByName ||
                       formByCustomElement ||
                       formInParent ||
                       (formByClosest instanceof HTMLFormElement ? formByClosest : formByClosest);
    
    if (this._productForm) {
      // Set up bundle picker if form is now available
      this._bundlePicker = this._productForm.querySelector('bundle-picker');
      // Add event listener if not already added
      if (this._onCartErrorListener && !this._productForm.hasAttribute('data-cart-error-listener-added')) {
        this._productForm.addEventListener('cart:error', this._onCartErrorListener);
        this._productForm.setAttribute('data-cart-error-listener-added', 'true');
      }
    }
  }

  disconnectedCallback() {
    this._abortController.abort();
    if (this._productForm && this._onCartErrorListener) {
      this._productForm.removeEventListener('cart:error', this._onCartErrorListener);
    }
  }

  _onCartError(event) {
    const errorBanner = document.createElement('div');
    errorBanner.classList.add('banner', 'banner--error', 'justify-center');
    errorBanner.setAttribute('role', 'alert');
    errorBanner.style.gridColumn = '1/-1';
    errorBanner.style.marginBottom = '1rem';
    errorBanner.innerHTML = `
      <svg role="presentation" focusable="false" width="18" height="18" class="offset-icon icon icon-error" style="--icon-height: 18px" viewBox="0 0 18 18">
        <path d="M0 9C0 4.02944 4.02944 0 9 0C13.9706 0 18 4.02944 18 9C18 13.9706 13.9706 18 9 18C4.02944 18 0 13.9706 0 9Z" fill="currentColor"></path>
        <path d="M5.29289 6.70711L11.2929 12.7071L12.7071 11.2929L6.70711 5.29289L5.29289 6.70711ZM6.70711 12.7071L12.7071 6.70711L11.2929 5.2929L5.29289 11.2929L6.70711 12.7071Z" fill="#ffffff"></path>
      </svg>
      <p>${event.detail.error}</p>
    `;
    this.before(errorBanner);
    setTimeout(async () => {
      await errorBanner.animate(
        { opacity: [1, 0] },
        { duration: 250, fill: 'forwards' }
      ).finished;
      errorBanner.remove();
    }, 5000);
  }

  _onSelectionChange(event) {
    const isOurPicker = this._bundlePicker && event.target === this._bundlePicker;
    const isOurForm = this._productForm && (event.target === this._productForm || event.target.closest('product-form-bundle') === this._productForm);
    const noPickerAssigned = !this._bundlePicker;

    if (isOurPicker || isOurForm || noPickerAssigned) {
      this._updateButton(event.detail);
    }
  }

  _getNonSubscriptionTotal(detail) {
    // Try to find form again if not found (might have been added dynamically)
    if (!this._productForm) {
      const formId = this.getAttribute('form');
      
      if (formId) {
        this._retryFormLookup(formId);
      }
    }
    
    if (!this._productForm) {
      return null;
    }
    const productFormBundle = this._productForm;
    if (!productFormBundle?._getBundleDiscountPercentage) {
      return null;
    }

    const sellingPlanId = this._productForm.querySelector('input[name="selling_plan"]:checked')?.value;
    // Only show non-subscription price when a selling plan IS selected
    if (!sellingPlanId || sellingPlanId === '') {
      return null;
    }

    // Get selected items - prefer detail.selectedItems, fallback to bundlePicker, then try form directly
    let selectedItems = null;
    
    if (detail?.selectedItems && Array.isArray(detail.selectedItems) && detail.selectedItems.length > 0) {
      // Items from ProductFormBundle event have: { id, quantity, price (base), discountedPrice }
      // We want the base price (without selling plan discount) but with bundle discount
      selectedItems = detail.selectedItems.map(item => ({
        quantity: Number(item.quantity) || 0,
        price: Number(item.price) || 0  // Use base price, not discountedPrice
      })).filter(item => item.price > 0 && item.quantity > 0);
    } else if (this._bundlePicker) {
      const items = this._bundlePicker.getSelectedItems();
      if (items && Array.isArray(items) && items.length > 0) {
        selectedItems = items.map(item => ({
          quantity: Number(item.quantity) || 0,
          price: Number(item.basePrice || item.price) || 0
        })).filter(item => item.price > 0 && item.quantity > 0);
      }
    }
    
    // Last resort: try to get items directly from form
    if ((!selectedItems || selectedItems.length === 0) && this._productForm) {
      const checkboxes = this._productForm.querySelectorAll('input[name="id"]:checked');
      if (checkboxes.length > 0) {
        selectedItems = Array.from(checkboxes).map(input => {
          const variantCard = input.closest('bundle-variant-card');
          const quantityInput = variantCard?.querySelector('input[type="number"][is="quantity-input"]');
          const quantity = quantityInput ? parseInt(quantityInput.value || '1', 10) : parseInt(input.dataset.productQty || '1', 10);
          const price = parseFloat(input.dataset.price || '0', 10);
          return { quantity, price };
        }).filter(item => item.price > 0 && item.quantity > 0);
      }
    }
    
    if (!selectedItems || selectedItems.length === 0) {
      return null;
    }

    const totalQuantity = selectedItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
    const bundleDiscountPercent = productFormBundle._getBundleDiscountPercentage(totalQuantity);
    const bundleDiscountMultiplier = (100 - bundleDiscountPercent) / 100;

    const nonSubscriptionTotal = selectedItems.reduce((sum, item) => {
      // Prices are in cents, apply bundle discount, round to nearest 100 cents (whole euro)
      const discountedUnitPrice = Math.round(item.price * bundleDiscountMultiplier);
      const roundedUnitPrice = Math.round(discountedUnitPrice / 100) * 100;
      const itemTotal = roundedUnitPrice * (item.quantity || 0);
      return sum + itemTotal;
    }, 0);

    const finalTotal = Math.round(nonSubscriptionTotal / 100) * 100;
    return finalTotal;
  }

  _updateNonSubscriptionPrice(nonSubscriptionTotal) {
    // Always query fresh to ensure we find the element even if it was hidden
    if (!this._button) {
      return;
    }
    
    const hasContentElement = this._button.contentElement && typeof this._button.contentElement === 'object';
    const targetElement = hasContentElement ? this._button.contentElement : this._button;
    
    // Query for the element - querySelector works even if element has display:none
    const span = targetElement.querySelector('[data-non-subscription-price]');
    
    if (!span) {
      return;
    }
    
    // Update stored reference
    this._nonSubscriptionPriceSpan = span;
    
    // Update the price display
    if (nonSubscriptionTotal !== null && nonSubscriptionTotal !== undefined && nonSubscriptionTotal > 0) {
      const formattedPrice = this._currencyFormatter.format(nonSubscriptionTotal / 100);
      span.textContent = formattedPrice;
      // Make sure element is visible (remove display:none if it was hidden)
      span.style.display = '';
      span.style.visibility = '';
      // If it's a <s> tag, ensure strikethrough is applied
      if (span.tagName === 'S' || span.tagName === 's') {
        span.style.textDecoration = 'line-through';
      }
    } else {
      // Hide if no valid total
      span.style.display = 'none';
    }
  }

  _updateButton(detail) {
    if (!this._button) return;

    let totalQty = 0;
    let totalPrice = 0;
    let selectedCount = 0;

    // Use discounted totals from event if available
    if (detail?.totals) {
      totalQty = detail.totals.quantity || 0;
      totalPrice = detail.totals.price || 0;
      selectedCount = detail.selectedCount || 0;
    } else if (this._bundlePicker) {
      const items = this._bundlePicker.getSelectedItems();
      selectedCount = items.length;
      items.forEach((item) => {
        totalQty += item.quantity || 0;
        totalPrice += (item.quantity || 0) * (item.price || 0);
      });
    } else if (detail?.selectedItems) {
      selectedCount = detail.selectedItems.length;
      detail.selectedItems.forEach((item) => {
        totalQty += item.quantity || 0;
        totalPrice += (item.quantity || 0) * (item.price || 0);
      });
    }

    const hasContentElement = this._button.contentElement && typeof this._button.contentElement === 'object';
    const targetElement = hasContentElement ? this._button.contentElement : this._button;
    let qtySpan = targetElement.querySelector('[data-total-qty]');
    let priceSpan = targetElement.querySelector('[data-total-price]');
    let nonSubscriptionPriceSpan = targetElement.querySelector('[data-non-subscription-price]');

    if (selectedCount === 0) {
      targetElement.textContent = window.themeVariables.strings.formSelectAtLeastOneProduct;
      this._button.disabled = true;
      this._qtySpan = null;
      this._priceSpan = null;
      this._nonSubscriptionPriceSpan = null;
    } else if (selectedCount === 1) {
      // Always update template for single item (in case we switched from multiple items)
      if (!priceSpan || !nonSubscriptionPriceSpan || qtySpan) {
        targetElement.innerHTML = window.themeVariables.strings.addToCartFor || 
          'Add to cart • <span data-non-subscription-price></span> • <span data-total-price></span>';
        // Re-query after setting innerHTML to get fresh references
        priceSpan = targetElement.querySelector('[data-total-price]');
        nonSubscriptionPriceSpan = targetElement.querySelector('[data-non-subscription-price]');
      }
      if (priceSpan) {
        const roundedPrice = Math.round(totalPrice / 100) * 100;
        priceSpan.textContent = this._currencyFormatter.format(roundedPrice / 100);
      }
      this._button.disabled = false;
      this._qtySpan = null;
      this._priceSpan = priceSpan;
      // Always update the reference even if it was found before, in case template was just updated
      this._nonSubscriptionPriceSpan = nonSubscriptionPriceSpan || targetElement.querySelector('[data-non-subscription-price]');
    } else {
      // Always update template for multiple items (in case we switched from single item)
      if (!qtySpan || !priceSpan || !nonSubscriptionPriceSpan) {
        targetElement.innerHTML = window.themeVariables.strings.buyAllItemsFor;
        // Re-query after setting innerHTML to get fresh references
        qtySpan = targetElement.querySelector('[data-total-qty]');
        priceSpan = targetElement.querySelector('[data-total-price]');
        nonSubscriptionPriceSpan = targetElement.querySelector('[data-non-subscription-price]');
      }
      if (qtySpan) qtySpan.textContent = totalQty;
      if (priceSpan) {
        const roundedPrice = Math.round(totalPrice / 100) * 100;
        priceSpan.textContent = this._currencyFormatter.format(roundedPrice / 100);
      }
      this._button.disabled = false;
      this._qtySpan = qtySpan;
      this._priceSpan = priceSpan;
      // Always update the reference even if it was found before, in case template was just updated
      this._nonSubscriptionPriceSpan = nonSubscriptionPriceSpan || targetElement.querySelector('[data-non-subscription-price]');
    }

    // Update non-subscription price display
    // Use requestAnimationFrame to ensure DOM is fully updated after innerHTML changes
    requestAnimationFrame(() => {
      const nonSubscriptionTotal = this._getNonSubscriptionTotal(detail);
      this._updateNonSubscriptionPrice(nonSubscriptionTotal);
    });
  }
}

if (!window.customElements.get('bundle-buy-buttons')) {
  window.customElements.define('bundle-buy-buttons', BundleBuyButtons);
}

var ProductFormBundle = class extends ProductForm {
  #submitting = false;
  _currencyFormatter = new Intl.NumberFormat(Shopify.locale, {
    style: 'currency',
    currency: Shopify.currency.active,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  _bundleDiscounts = null;
  _variantDataCache = new Map();
  _sellingPlans = null;

  constructor() {
    super();
    this.addEventListener('submit', this._onSubmit);
    this._addCheckboxListeners();
    this._loadBundleDiscounts();
    this._loadSellingPlans();
  }

  connectedCallback() {
    // Reload discounts in case script wasn't available in constructor
    if (!this._bundleDiscounts || Object.keys(this._bundleDiscounts).length === 0) {
      this._loadBundleDiscounts();
    }
    // Reload selling plans in case script wasn't available in constructor
    if (!this._sellingPlans || this._sellingPlans.length === 0) {
      this._loadSellingPlans();
    }
    // Initialize totals and dispatch initial event
    this._handleSelectionChange();
  }

  _loadBundleDiscounts() {
    // Try to find the script in the form first, then globally
    let discountScript = this.querySelector('#form-bundle-discounts');
    if (!discountScript) {
      discountScript = document.getElementById('form-bundle-discounts');
    }
    
    if (discountScript) {
      try {
        this._bundleDiscounts = JSON.parse(discountScript.textContent);
      } catch (e) {
        this._bundleDiscounts = {};
      }
    } else {
      this._bundleDiscounts = {};
    }
  }

  _loadSellingPlans() {
    // Try to find the script in the form first, then globally
    let sellingPlansScript = this.querySelector('#form-bundle-selling-plans');
    if (!sellingPlansScript) {
      sellingPlansScript = document.getElementById('form-bundle-selling-plans');
    }
    
    if (sellingPlansScript) {
      try {
        // The script contains a JSON array, but might need to be wrapped in brackets
        const scriptContent = sellingPlansScript.textContent.trim();
        let parsed;
        
        // Try parsing as array first
        if (scriptContent.startsWith('[')) {
          parsed = JSON.parse(scriptContent);
        } else {
          // If not an array, wrap it in brackets
          parsed = JSON.parse('[' + scriptContent + ']');
        }
        
        this._sellingPlans = Array.isArray(parsed) ? parsed : [parsed];
      } catch (e) {
        this._sellingPlans = [];
      }
    } else {
      this._sellingPlans = [];
    }
  }

  _getSellingPlanById(sellingPlanId) {
    if (!this._sellingPlans || this._sellingPlans.length === 0) {
      return null;
    }
    
    const plan = this._sellingPlans.find(p => p.id === parseInt(sellingPlanId, 10));
    
    return plan || null;
  }

  _getSellingPlanDiscountPercent(sellingPlanId) {
    const plan = this._getSellingPlanById(sellingPlanId);
    if (!plan || !plan.price_adjustments || plan.price_adjustments.length === 0) {
      return 0;
    }
    
    // Get the first price adjustment (usually there's only one)
    const adjustment = plan.price_adjustments[0];
    let discountPercent = 0;
    
    if (adjustment.value_type === 'percentage') {
      discountPercent = parseFloat(adjustment.value) || 0;
    } else if (adjustment.value_type === 'fixed_amount') {
      // For fixed amount, we'd need the base price to calculate percentage
      // For now, return 0
    }
    
    return discountPercent;
  }

  _getBundleDiscountPercentage(totalQuantity) {
    if (!this._bundleDiscounts || Object.keys(this._bundleDiscounts).length === 0) {
      return 0;
    }

    // Find the highest discount level that applies to the total quantity
    let maxDiscount = 0;
    let applicableLevel = 0;

    for (const [level, discount] of Object.entries(this._bundleDiscounts)) {
      const levelNum = parseInt(level, 10);
      const discountNum = parseInt(discount, 10);
      if (totalQuantity >= levelNum && discountNum > maxDiscount) {
        maxDiscount = discountNum;
        applicableLevel = levelNum;
      }
    }

    return maxDiscount;
  }

  _getSelectedSellingPlanId() {
    const sellingPlanInput = this.querySelector('input[name="selling_plan"]:checked');
    const sellingPlanId = sellingPlanInput?.value || null;
    return sellingPlanId;
  }

  async _getVariantWithSellingPlan(variantId, sellingPlanId) {
    if (!sellingPlanId) {
      return null;
    }

    // Check cache first
    const cacheKey = `${variantId}-${sellingPlanId}`;
    if (this._variantDataCache.has(cacheKey)) {
      const cached = this._variantDataCache.get(cacheKey);
      return cached;
    }

    try {
      // Try to get variant data from script tags on the page
      const variantScripts = document.querySelectorAll('script[data-variant]');
      
      for (const script of variantScripts) {
        try {
          const variantData = JSON.parse(script.textContent);
          if (variantData?.id === variantId) {
            if (variantData?.selling_plan_allocations) {
              const allocation = variantData.selling_plan_allocations.find(
                (alloc) => {
                  const matchesId = alloc.selling_plan_id === parseInt(sellingPlanId, 10);
                  const matchesNested = alloc.selling_plan?.id === parseInt(sellingPlanId, 10);
                  return matchesId || matchesNested;
                }
              );
              if (allocation) {
                this._variantDataCache.set(cacheKey, allocation);
                return allocation;
              }
            }
          }
        } catch (e) {
          // Skip invalid JSON
          continue;
        }
      }

      // Fallback: fetch variant data
      const variantUrl = `${Shopify.routes.root}variants/${variantId}`;
      const response = await fetch(variantUrl);
      if (response.ok) {
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const variantScript = doc.querySelector('script[data-variant]');
        if (variantScript) {
          const variantData = JSON.parse(variantScript.textContent);
          if (variantData?.selling_plan_allocations) {
            const allocation = variantData.selling_plan_allocations.find(
              (alloc) => alloc.selling_plan_id === parseInt(sellingPlanId, 10) || alloc.selling_plan?.id === parseInt(sellingPlanId, 10)
            );
            if (allocation) {
              this._variantDataCache.set(cacheKey, allocation);
              return allocation;
            }
          }
        }
      }
    } catch (e) {
      // Failed to get variant with selling plan
    }

    return null;
  }

  async _getDiscountedPrice(variantId, basePrice, sellingPlanId) {
    let price = basePrice;

    // Apply selling plan discount if available
    if (sellingPlanId) {
      // First try to get discount from selling plan definition
      const discountPercent = this._getSellingPlanDiscountPercent(sellingPlanId);
      
      if (discountPercent > 0) {
        // Calculate price with discount
        const discountMultiplier = (100 - discountPercent) / 100;
        price = Math.round(basePrice * discountMultiplier);
      } else {
        // Fallback: try to get from variant allocation
        const allocation = await this._getVariantWithSellingPlan(variantId, sellingPlanId);
        if (allocation?.price) {
          price = allocation.price;
        }
      }
    }

    return price;
  }

  async getSelectedProducts() {
    const selectedProducts = [];
    const sellingPlanId = this._getSelectedSellingPlanId();
    const promises = [];

    this.querySelectorAll('input[name="id"]:checked').forEach((input) => {
      const variantCard = input.closest('bundle-variant-card');
      const quantityInput = variantCard?.querySelector('input[type="number"][is="quantity-input"]');
      const quantity = quantityInput ? parseInt(quantityInput.value || '1', 10) : parseInt(input.getAttribute('data-product-qty') || '1', 10);
      const variantId = parseInt(input.value, 10);
      const basePrice = parseFloat(input.getAttribute('data-price')) || 0;

      promises.push(
        this._getDiscountedPrice(variantId, basePrice, sellingPlanId).then((discountedPrice) => {
      selectedProducts.push({
            id: variantId,
        quantity: quantity,
            price: basePrice,
            discountedPrice: discountedPrice,
          });
        })
      );
    });

    await Promise.all(promises);
    return selectedProducts;
  }

  _getSelectedProductsSync() {
    // Synchronous version that uses cached data when possible
    const selectedProducts = [];
    
    if (!this || !this.querySelectorAll) {
      return selectedProducts;
    }

    const sellingPlanId = this._getSelectedSellingPlanId();

    this.querySelectorAll('input[name="id"]:checked').forEach((input) => {
      const variantCard = input.closest('bundle-variant-card');
      const quantityInput = variantCard?.querySelector('input[type="number"][is="quantity-input"]');
      const quantity = quantityInput ? parseInt(quantityInput.value || '1', 10) : parseInt(input.getAttribute('data-product-qty') || '1', 10);
      const variantId = parseInt(input.value, 10);
      const basePrice = parseFloat(input.getAttribute('data-price')) || 0;

      // Calculate selling plan discount using the plan definition
      let discountedPrice = basePrice;
      if (sellingPlanId) {
        const discountPercent = this._getSellingPlanDiscountPercent(sellingPlanId);
        if (discountPercent > 0) {
          const discountMultiplier = (100 - discountPercent) / 100;
          discountedPrice = Math.round(basePrice * discountMultiplier);
        } else {
          // Fallback: try cache
          const cacheKey = `${variantId}-${sellingPlanId}`;
          const allocation = this._variantDataCache?.get(cacheKey);
          if (allocation?.price) {
            discountedPrice = allocation.price;
          }
        }
      }

      selectedProducts.push({
        id: variantId,
        quantity: quantity,
        price: basePrice,
        discountedPrice: discountedPrice,
      });
    });

    return selectedProducts;
  }

  _getFreeGifts() {
    const formId = this.id || this.getAttribute('id');
    
    // Collect all checked free gift checkboxes - try multiple approaches
    // This ensures we find checkboxes even if they're in a drawer outside the form
    let freeGiftCheckboxes = [];
    
    // Method 1: Query by form attribute (works when drawer is rendered and checkboxes are associated with this form)
    if (formId) {
      freeGiftCheckboxes = Array.from(
        document.querySelectorAll(`input[name="after_sell_items"][form="${formId}"]:checked`)
      );
    }
    
    // Method 2: Fallback - find all checked free gift checkboxes in free-gift-section
    // This works even if checkboxes are associated with a different form (like the main product form)
    if (freeGiftCheckboxes.length === 0) {
      const freeGiftSections = document.querySelectorAll('free-gift-section');
      freeGiftSections.forEach(section => {
        const checkboxes = Array.from(section.querySelectorAll('input[name="after_sell_items"]:checked'));
        freeGiftCheckboxes.push(...checkboxes);
      });
    }
    
    // Method 3: Last resort - query all checked after_sell_items checkboxes within free-gift-item elements
    // This ensures we find them even if the drawer structure is different
    if (freeGiftCheckboxes.length === 0) {
      const allChecked = Array.from(document.querySelectorAll('input[name="after_sell_items"]:checked'));
      freeGiftCheckboxes = allChecked.filter(checkbox => 
        checkbox.closest('free-gift-item') !== null
      );
    }
    
    // Also check within the form itself (for backward compatibility)
    const formCheckboxes = Array.from(this.querySelectorAll('input[name="after_sell_items"]:checked'));
    formCheckboxes.forEach(checkbox => {
      if (!freeGiftCheckboxes.includes(checkbox)) {
        freeGiftCheckboxes.push(checkbox);
      }
    });
    
    const freeGifts = [];
    const seenUniqueIds = new Set();

    freeGiftCheckboxes.forEach((checkbox) => {
      const variantId = parseInt(checkbox.value, 10);
      const dataKey = checkbox.dataset.key;
      
      if (!variantId || isNaN(variantId) || !dataKey) {
        return;
      }

      // Parse data-key format: {variantId}-{source_product_id}
      const parts = dataKey.split('-');
      if (parts.length < 2) {
        return;
      }

      // Get source product ID (everything after the first hyphen)
      const sourceProductId = parts.slice(1).join('-');

      // Construct uniqueId the same way as regular productForm: {variantId}-{productId}
      const uniqueId = `${variantId}-${sourceProductId}`;

      if (seenUniqueIds.has(uniqueId)) {
        return;
      }
      seenUniqueIds.add(uniqueId);
      freeGifts.push({
        variantId: variantId,
        sourceProductId: sourceProductId,
        uniqueId: uniqueId
      });
    });

    return freeGifts;
  }

  async _onSubmit(event) {
    event.preventDefault();

    if (this.#submitting) return;

    // ⏱️ PERFORMANCE TIMING & DEBUG
    const _t0 = performance.now();
    const _debug = {
      steps: [],
      errors: [],
      info: {},
      log: (step, time) => {
        const entry = `${step}: ${time.toFixed(0)}ms`;
        _debug.steps.push(entry);
        console.log(`%c[ProductFormBundle] ${entry}`, 'color: #4caf50');
      }
    };
    console.log('%c[ProductFormBundle] ⏱️ Bundle add to cart started', 'color: #00bcd4; font-weight: bold');

    if (!this.checkValidity()) {
      this.reportValidity();
      return;
    }

    const submitButtons = Array.from(this.elements).filter(
      (button) => button.type === 'submit'
    );
    submitButtons.forEach((submitButton) =>
      submitButton.setAttribute('aria-busy', 'true')
    );

    this.#submitting = true;

    const _tProducts = performance.now();
    const products = await this.getSelectedProducts();
    _debug.log('⚡ getSelectedProducts', performance.now() - _tProducts);
    _debug.info.productCount = products.length;
    _debug.info.productIds = products.map(p => p.id).join(', ');

    if (products.length === 0) {
      this.#submitting = false;
      submitButtons.forEach((submitButton) =>
        submitButton.removeAttribute('aria-busy')
      );
      return;
    }

    const sectionsToBundle = ['variant-added'];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      })
    );

    const formData = new FormData();

    // Get the selected selling plan from the form
    const sellingPlanInput = this.querySelector('input[name="selling_plan"]:checked');
    const sellingPlanId = sellingPlanInput?.value || null;
    const hasSellingPlan = sellingPlanId && sellingPlanId !== '';

    // Collect free gifts only if a selling plan is selected
    // Free gifts should only be added for subscription purchases
    const freeGifts = hasSellingPlan ? this._getFreeGifts() : [];
    
    // Get the bundle product ID (all variants in a bundle belong to the same product)
    const bundleProductId = this.dataset.productId;
    
    // Filter free gifts that match this bundle product
    const bundleFreeGifts = freeGifts.filter((gift) => {
      return gift.sourceProductId === bundleProductId;
    });
    
    // Normalize free gift properties based on existing cart items
    // This ensures consistent ordering when items are added multiple times
    const _tNormalize = performance.now();
    const normalizedGiftData = await this._normalizeFreeGiftProperties(
      bundleProductId,
      bundleFreeGifts,
      hasSellingPlan
    );
    _debug.log('📋 normalizeFreeGiftProperties', performance.now() - _tNormalize);
    _debug.info.hasSellingPlan = hasSellingPlan;
    _debug.info.freeGiftsCount = bundleFreeGifts.length;
    
    // Calculate gift count and IDs for the bundle ONCE
    // All variants of the same product will share these same properties
    // Only count gifts if a selling plan is selected
    const giftCount = normalizedGiftData.count;
    const assignedGiftIds = normalizedGiftData.ids;
    const cachedCart = normalizedGiftData.cart; // Reuse cart to avoid re-fetching

    // Add all bundle variants - they all share the same free gift properties
    // since they belong to the same product
    const _t1 = performance.now();
    let itemIndex = 0;
    
    for (let i = 0; i < products.length; i++) {
      const product = products[i];
      formData.append(`items[${itemIndex}][id]`, product.id);
      formData.append(`items[${itemIndex}][quantity]`, product.quantity);
      
      // Add selling_plan if one is selected (and not empty string)
      if (sellingPlanId && sellingPlanId !== '') {
        formData.append(`items[${itemIndex}][selling_plan]`, sellingPlanId);
      }
      
      // All variants get the same free gift properties (giftCount, assignedGiftIds)
      // Note: _addProductProperties is now SYNC - instant DOM lookup, no network
      this._addProductProperties(formData, product, itemIndex, sellingPlanId, giftCount, assignedGiftIds);
      itemIndex++;
    }

    // OPTIMIZATION: Add free gifts to the SAME request (saves ~900ms separate API call!)
    let giftsAddedCount = 0;
    if (hasSellingPlan && bundleFreeGifts.length > 0) {
      const giftLimit = window.themeVariables?.freeGiftsLimitPerProduct ?? 3;
      
      // Count existing gifts in cart (using cached cart)
      const existingGiftCount = cachedCart?.items?.filter(item => {
        return item.properties?._free_gift === 'true' &&
               item.properties?._related_line_item_id === bundleProductId;
      }).length || 0;
      
      // Calculate remaining slots
      const remainingSlots = Math.max(0, giftLimit - existingGiftCount);
      const giftsToAdd = bundleFreeGifts.slice(0, remainingSlots);
      
      // Build set of existing unique IDs to avoid duplicates
      const existingUniqueIds = new Set();
      cachedCart?.items?.forEach(item => {
        if (item.properties?._unique_id) {
          existingUniqueIds.add(item.properties._unique_id);
        }
      });
      
      // Add gifts to the same formData
      for (const gift of giftsToAdd) {
        const uniqueId = `${gift.variantId}-${bundleProductId}`;
        
        // Skip if already in cart
        if (existingUniqueIds.has(uniqueId)) continue;
        
        formData.append(`items[${itemIndex}][id]`, gift.variantId);
        formData.append(`items[${itemIndex}][quantity]`, '1');
        formData.append(`items[${itemIndex}][properties][_free_gift]`, 'true');
        formData.append(`items[${itemIndex}][properties][_unique_id]`, uniqueId);
        formData.append(`items[${itemIndex}][properties][_related_line_item_id]`, bundleProductId);
        itemIndex++;
        giftsAddedCount++;
      }
      _debug.info.giftsAddedInSameRequest = giftsAddedCount;
    }
    
    _debug.log('⚡ Build form data', performance.now() - _t1);

    formData.set('sections', sectionsToBundle.join(','));
    const firstVariantId = products.length > 0 ? products[0].id : (this.id?.value || '');
    formData.set(
      'sections_url',
      `${Shopify.routes.root}variants/${firstVariantId}`
    );

    try {
      const _t2 = performance.now();
      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        body: formData,
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      const responseJson = await response.json();
      _debug.log('🛒 cart/add.js (bundle + gifts)', performance.now() - _t2);
      _debug.info.cartAddStatus = response.status;

      if (response.ok) {
        // Get the added items from the response to find their product IDs
        const addedItems = Array.isArray(responseJson.items) ? responseJson.items : [responseJson];
        _debug.info.addedItemsCount = addedItems.length;

        // Free gifts are now added in the same request above - no separate call needed!

        const picker = this.querySelector('bundle-picker');
        if (picker && typeof picker.reset === 'function') {
          picker.reset();
        }
        
        // Fetch cart ONCE after items are added
        const _tCart = performance.now();
        let cartContent = await fetch(`${Shopify.routes.root}cart.js`).then(r => r.json());
        _debug.log('📦 cart.js fetch', performance.now() - _tCart);

        // Update existing items with normalized free gift properties to ensure consistency
        // OPTIMIZATION: Fire-and-forget - don't block UI for background consistency updates
        if (hasSellingPlan && bundleProductId && normalizedGiftData.ids) {
          // Start updates in background - don't await!
          this._updateExistingItemsWithNormalizedGifts(
            bundleProductId,
            normalizedGiftData.count,
            normalizedGiftData.ids,
            cartContent
          ).catch(() => {}); // Silently handle errors
          _debug.steps.push('🔄 updateExistingItems (fire-and-forget)');
        }

        // Merge sections from both bundle and free gift responses
        cartContent['sections'] = responseJson['sections'] || [];

        // OPTIMIZATION: Fire cart/update.js but DON'T wait for it (non-blocking)
        try {
          const nextAttrs = CartDrawer.computeCartAttributesFromItems(cartContent.items || []);
          const needAttrUpdate = Object.entries(nextAttrs).some(([k, v]) => String(cartContent?.attributes?.[k] ?? '') !== v);
          _debug.info.needAttrUpdate = needAttrUpdate;
          if (needAttrUpdate) {
            // Fire and forget - don't block UI
            fetch(`${Shopify.routes.root}cart/update.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ attributes: nextAttrs })
            }).catch(() => {});
            // Optimistically update attributes for immediate UI
            cartContent.attributes = { ...cartContent.attributes, ...nextAttrs };
          }
        } catch (e) {
          // Failed to update cart attributes
        }

        if (this.closest('x-drawer')) {
          const relatedDrawer = this.closest('x-drawer');
          await relatedDrawer.hide();
          this._dispatchCartChange(cartContent, responseJson);
        } else {
          this._dispatchCartChange(cartContent, responseJson);
        }
      } else {
        _debug.errors.push(`Cart error: ${responseJson['description'] || 'Unknown'}`);
        this.dispatchEvent(
          new CustomEvent('cart:error', {
            bubbles: true,
            detail: {
              error: responseJson['description'] || 'Unknown error occurred',
            },
          })
        );
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    } catch (error) {
      // Error adding to cart
      console.error('[ProductFormBundle] Error:', error);
      _debug.errors.push(`Exception: ${error.message}`);
    } finally {
      // ⏱️ PERFORMANCE TIMING END
      const totalTime = performance.now() - _t0;
      _debug.info.totalTime = totalTime.toFixed(0) + 'ms';
      console.log(`%c[ProductFormBundle] ✅ TOTAL TIME: ${totalTime.toFixed(1)}ms`, 'color: #4caf50; font-weight: bold; font-size: 14px');
      
      this.#submitting = false;
      submitButtons.forEach((submitButton) =>
        submitButton.removeAttribute('aria-busy')
      );
    }
  }

  async _addFreeGifts(freeGifts, addedBundleItems, sectionsToBundle, existingCart = null) {
    if (!freeGifts || freeGifts.length === 0) {
      return;
    }

    // Get the gift limit from theme variables or default to 3
    const giftLimit = window.themeVariables?.freeGiftsLimitPerProduct ?? 3;

    // OPTIMIZATION: Use existing cart if provided, otherwise fetch (saves ~400ms)
    let currentCart = existingCart;
    if (!currentCart) {
      try {
        const cartResponse = await fetch(`${Shopify.routes.root}cart.js`);
        currentCart = await cartResponse.json();
      } catch (error) {
        // If we can't fetch cart, proceed without limit check (fallback)
        currentCart = { items: [] };
      }
    }

    // Create a map of product_id -> {line_item_key, product_id} for matching free gifts to bundle items
    const productIdToLineItemData = new Map();
    
    addedBundleItems.forEach((item) => {
      if (item.product_id && item.key) {
        // Extract the line item key (before the colon if present)
        const lineItemKey = item.key.split(':')[0];
        productIdToLineItemData.set(item.product_id.toString(), {
          lineItemKey: lineItemKey,
          productId: item.product_id.toString()
        });
      }
    });

    // Filter free gifts to only those that match added bundle items
    const validFreeGifts = freeGifts.filter((gift) => {
      return productIdToLineItemData.has(gift.sourceProductId);
    });

    if (validFreeGifts.length === 0) {
      return;
    }

    // Group gifts by product ID and enforce limit per product
    const giftsByProduct = new Map();
    validFreeGifts.forEach((gift) => {
      const productId = gift.sourceProductId;
      if (!giftsByProduct.has(productId)) {
        giftsByProduct.set(productId, []);
      }
      giftsByProduct.get(productId).push(gift);
    });

    // Count existing gifts in cart for each product and filter new gifts to respect limit
    const giftsToAdd = [];
    for (const [productId, productGifts] of giftsByProduct.entries()) {
      // Count existing gifts in cart for this product
      const existingGiftCount = currentCart.items?.filter(item => {
        return item.properties?._free_gift === 'true' &&
               item.properties?._related_line_item_id === productId;
      }).length || 0;

      // Calculate how many new gifts we can add
      const remainingSlots = Math.max(0, giftLimit - existingGiftCount);
      
      // Only add gifts up to the remaining slots
      const giftsToAddForProduct = productGifts.slice(0, remainingSlots);
      giftsToAdd.push(...giftsToAddForProduct);
    }

    if (giftsToAdd.length === 0) {
      return;
    }

    const freeGiftsFormData = new FormData();

    // Add each free gift using the same format as ProductForm
    for (let i = 0; i < giftsToAdd.length; i++) {
      const gift = giftsToAdd[i];
      const lineItemData = productIdToLineItemData.get(gift.sourceProductId);

      if (!lineItemData) {
        continue;
      }

      // Construct uniqueId using the actual bundle item's product_id (same format as regular productForm)
      const uniqueId = `${gift.variantId}-${lineItemData.productId}`;

      freeGiftsFormData.append(`items[${i}][id]`, gift.variantId);
      freeGiftsFormData.append(`items[${i}][quantity]`, '1');
      freeGiftsFormData.append(`items[${i}][properties][_free_gift]`, 'true');
      freeGiftsFormData.append(`items[${i}][properties][_unique_id]`, uniqueId);
      // Use productId for _related_line_item_id to match regular productForm format
      // The last part of uniqueId should equal _related_line_item_id
      freeGiftsFormData.append(`items[${i}][properties][_related_line_item_id]`, lineItemData.productId);
    }

    // Add sections to FormData
    freeGiftsFormData.set('sections', sectionsToBundle.join(','));
    const firstGiftVariantId = giftsToAdd[0]?.variantId;
    if (firstGiftVariantId) {
      freeGiftsFormData.set(
        'sections_url',
        `${Shopify.routes.root}variants/${firstGiftVariantId}`
      );
    }

    try {
      const batchResponse = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        body: freeGiftsFormData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!batchResponse.ok) {
        // Failed to add batched free gifts
      }
    } catch (error) {
      // Error adding free gifts
    }
  }

  /**
   * Update existing cart items with normalized free gift properties
   * This ensures all items with the same product_id have consistent gift IDs ordering
   * @param {string} productId - The product ID to update
   * @param {number} normalizedCount - Normalized gift count
   * @param {string} normalizedIds - Normalized gift IDs (comma-separated, sorted)
   * @param {Object} cart - Current cart object
   * @returns {Promise<boolean>} - Whether any changes were made
   */
  async _updateExistingItemsWithNormalizedGifts(productId, normalizedCount, normalizedIds, cart) {
    if (!productId || !cart?.items || !normalizedIds) return false;

    // Helper function to normalize gift IDs for comparison
    const normalizeIds = (idsString) => {
      if (!idsString) return '';
      return idsString.split(',').filter(Boolean).sort((a, b) => parseInt(a, 10) - parseInt(b, 10)).join(',');
    };

    // Normalize the target IDs for comparison
    const normalizedIdsSorted = normalizeIds(normalizedIds);

    // Find items that ACTUALLY need updating (not already correct)
    const itemsToUpdate = cart.items.filter(item => {
      if (item.product_id?.toString() !== productId.toString()) return false;
      if (item.properties?._eligible_for_free_gifts !== 'true') return false;
      if (item.selling_plan_allocation == null) return false;
      
      // Check if already has correct values - skip if so
      const currentIds = normalizeIds(item.properties?._assigned_free_gift_ids || '');
      const currentCount = item.properties?._free_gifts_count || '0';
      if (currentIds === normalizedIdsSorted && currentCount === normalizedCount.toString()) {
        return false; // Already correct, skip
      }
      return true;
    });

    if (itemsToUpdate.length === 0) return false;

    // OPTIMIZATION: Run all updates in PARALLEL (saves ~300ms per item!)
    const updatePromises = itemsToUpdate.map(item => 
      fetch(`${Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: item.key,
          quantity: item.quantity,
          properties: {
            ...item.properties,
            _assigned_free_gift_ids: normalizedIds,
            _free_gifts_count: normalizedCount.toString(),
          },
        }),
      }).catch(() => null) // Ignore individual failures
    );

    await Promise.all(updatePromises);
    return true;
  }

  /**
   * Normalize free gift properties based on existing cart items
   * This ensures consistent ordering when items are added multiple times
   * Matches ProductForm behavior: use existing product's properties as base, then merge with new selections
   * @param {string} productId - The product ID to check
   * @param {Array} selectedGifts - Array of selected free gifts from the form
   * @param {boolean} hasSellingPlan - Whether a selling plan is selected
   * @returns {Promise<{count: number, ids: string}>} Normalized gift count and IDs
   */
  async _normalizeFreeGiftProperties(productId, selectedGifts, hasSellingPlan) {
    if (!hasSellingPlan || !productId) {
      return { count: 0, ids: '', cart: null };
    }

    // OPTIMIZATION: If no gifts selected, skip cart fetch entirely (saves ~400-800ms!)
    if (!selectedGifts || selectedGifts.length === 0) {
      return { count: 0, ids: '', cart: null };
    }

    try {
      // Fetch current cart to check for existing items and gifts
      const cartResponse = await fetch(`${Shopify.routes.root}cart.js`);
      const currentCart = await cartResponse.json();

      if (!currentCart?.items) {
        // No cart items, use selected gifts as-is (sorted for consistency)
        const giftIds = selectedGifts.map((gift) => gift.variantId.toString()).sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
        return {
          count: giftIds.length,
          ids: giftIds.join(',')
        };
      }

      // Find existing product with same product_id that has free-gift properties (like ProductForm does)
      const existingProduct = currentCart.items.find(item => {
        if (item.product_id?.toString() !== productId.toString()) return false;
        return item.properties?._eligible_for_free_gifts === 'true' ||
               item.properties?._free_gifts_count ||
               item.properties?._assigned_free_gift_ids;
      });

      // Get existing gift IDs from existing product's properties (if found)
      let existingGiftIds = [];
      if (existingProduct?.properties?._assigned_free_gift_ids) {
        existingGiftIds = existingProduct.properties._assigned_free_gift_ids
          .split(',')
          .filter(Boolean);
      }

      // Also get actual free gifts in cart for this product (source of truth)
      const actualGifts = currentCart.items.filter(item => 
        item.properties?._free_gift === 'true' && 
        item.properties?._related_line_item_id === productId.toString()
      );
      const actualGiftIds = actualGifts
        .map(gift => gift.variant_id?.toString())
        .filter(Boolean);

      // Get selected gift IDs from form
      const selectedGiftIds = selectedGifts
        .map(gift => gift.variantId.toString())
        .filter(Boolean);

      // Merge: use existing product's IDs as base, then merge with actual gifts and selected gifts
      // This matches ProductForm behavior: existing properties take precedence, then merge with new
      const allGiftIds = Array.from(new Set([
        ...existingGiftIds,  // Existing product's assigned IDs (preserves order)
        ...actualGiftIds,    // Actual gifts in cart
        ...selectedGiftIds   // New selections
      ]))
      .sort((a, b) => parseInt(a, 10) - parseInt(b, 10)); // Sort for consistency

      return {
        count: allGiftIds.length,
        ids: allGiftIds.join(','),
        cart: currentCart  // Pass cart to avoid re-fetching
      };
    } catch (error) {
      // Fallback: use selected gifts as-is if cart fetch fails
      const giftIds = selectedGifts.map((gift) => gift.variantId.toString()).sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
      return {
        count: giftIds.length,
        ids: giftIds.join(','),
        cart: null
      };
    }
  }

  _addProductProperties(formData, product, index, sellingPlanId = null, giftCount = 0, assignedGiftIds = '') {
    // Check if this product has a selling plan - if yes, it's eligible for free gifts
    const hasSellingPlan = sellingPlanId && sellingPlanId !== '';
    const isEligible = hasSellingPlan || (this.dataset.eligibleForFreeGifts === 'true');
    
    // Set properties with actual free gift data (already calculated before adding items)
    formData.append(`items[${index}][properties][_eligible_for_free_gifts]`, isEligible ? 'true' : 'false');
    formData.append(`items[${index}][properties][_free_gifts_count]`, giftCount.toString());
    formData.append(`items[${index}][properties][_assigned_free_gift_ids]`, assignedGiftIds);

    // Get real_qty INSTANTLY from embedded DOM data (no network request!)
    // Use window.getVariantRealQty which is defined in theme.js
    const realQty = window.getVariantRealQty ? window.getVariantRealQty(product.id) : 1;
    formData.append(`items[${index}][properties][_real_qty]`, String(realQty));
  }

  _dispatchCartChange(cartContent, responseJson) {
    this.dispatchEvent(
      new CustomEvent('variant:add', {
        bubbles: true,
        detail: {
          items: responseJson.hasOwnProperty('items')
            ? responseJson['items']
            : [responseJson],
          cart: cartContent,
        },
      })
    );

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          baseEvent: 'variant:add',
          cart: cartContent,
        },
      })
    );
  }

  _addCheckboxListeners() {
    this.querySelectorAll('input[name="id"]').forEach((checkbox) => {
      checkbox.addEventListener('change', (event) => {
        if (this.#submitting) return;
        this._handleSelectionChange();
      });
    });

    // Also listen to quantity changes
    this.querySelectorAll('input[type="number"][is="quantity-input"]').forEach((quantityInput) => {
      quantityInput.addEventListener('change', () => {
        if (this.#submitting) return;
        this._handleSelectionChange();
      });
      quantityInput.addEventListener('input', () => {
        if (this.#submitting) return;
        this._handleSelectionChange();
      });
    });

    // Listen to selling plan changes
    this.querySelectorAll('input[name="selling_plan"]').forEach((sellingPlanInput) => {
      sellingPlanInput.addEventListener('change', (event) => {
        if (this.#submitting) return;
        
        // Clear cache when selling plan changes
        this._variantDataCache.clear();
        
        this._handleSelectionChange();
      });
    });
  }

  _handleSelectionChange() {
    const selectedProducts = this._getSelectedProductsSync();
    if (!Array.isArray(selectedProducts)) return;
    const selectedCount = selectedProducts.length;

    // Update button state directly (for backward compatibility)
    const buyButton = this.querySelector('button[type="submit"]');
    if (buyButton) {
        const hasContentElement = buyButton.contentElement && typeof buyButton.contentElement === 'object';

      if (selectedCount === 0) {
          if (hasContentElement) {
            buyButton.contentElement.textContent = window.themeVariables.strings.formSelectProducts;
          } else {
            buyButton.textContent = window.themeVariables.strings.formSelectProducts;
          }
          buyButton.disabled = true;
      } else if (selectedCount === 1) {
          if (hasContentElement) {
            buyButton.contentElement.textContent = window.themeVariables.strings.addToCartButtonText;
          } else {
            buyButton.textContent = window.themeVariables.strings.addToCartButtonText;
          }
          buyButton.disabled = false;
        } else {
          if (hasContentElement) {
            buyButton.contentElement.innerHTML = window.themeVariables.strings.buyAllItemsFor;
          } else {
            buyButton.innerHTML = window.themeVariables.strings.buyAllItemsFor;
        }
        buyButton.disabled = false;
          }
        }

    // Update total data in DOM
        this._updateTotalData();

    // Dispatch event for other components like BundleBuyButtons
    const selectedItems = selectedProducts.map((product) => ({
      id: product.id,
      quantity: product.quantity,
      price: product.price,
      discountedPrice: product.discountedPrice,
    }));

    const totals = this.getTotals();

    this.dispatchEvent(
      new CustomEvent('bundle:selection-change', {
        bubbles: true,
        detail: {
          selectedCount,
          selectedItems,
          totals: {
            quantity: totals.quantity,
            price: totals.price,
            originalPrice: totals.originalPrice,
            savings: totals.savings,
            bundleDiscountPercent: totals.bundleDiscountPercent,
            averagePricePerItem: totals.averagePricePerItem,
            averagePricePerProduct: totals.averagePricePerProduct,
          },
          itemCosts: totals.items,
        },
      })
    );
  }

  getTotalQuantity() {
    const selectedProducts = this._getSelectedProductsSync();
    if (!Array.isArray(selectedProducts)) return 0;
    return selectedProducts.reduce((sum, product) => sum + (product.quantity || 0), 0);
  }

  _calculateFinalPrices(selectedProducts) {
    if (!Array.isArray(selectedProducts)) return [];
    const totalQuantity = selectedProducts.reduce((sum, product) => sum + (product.quantity || 0), 0);
    const bundleDiscountPercent = this._getBundleDiscountPercentage(totalQuantity);
    const bundleDiscountMultiplier = (100 - bundleDiscountPercent) / 100;

    return selectedProducts.map((product) => {
      // Start with selling plan discounted price (or base price)
      let finalUnitPrice = product.discountedPrice || product.price;
      
      // Apply bundle discount
      finalUnitPrice = Math.round(finalUnitPrice * bundleDiscountMultiplier);
      
      // Round unit price to nearest whole euro (100 cents)
      finalUnitPrice = Math.round(finalUnitPrice / 100) * 100;
      
      const totalPrice = finalUnitPrice * (product.quantity || 0);

      return {
        ...product,
        finalUnitPrice: finalUnitPrice,
        finalTotalPrice: totalPrice,
        bundleDiscountPercent: bundleDiscountPercent,
        originalUnitPrice: product.price,
        sellingPlanDiscountedPrice: product.discountedPrice || product.price,
      };
    });
  }

  getTotalPrice() {
    const selectedProducts = this._getSelectedProductsSync();
    if (!Array.isArray(selectedProducts)) return 0;
    const finalPrices = this._calculateFinalPrices(selectedProducts);
    if (!Array.isArray(finalPrices)) return 0;
    const total = finalPrices.reduce((sum, product) => sum + (product.finalTotalPrice || 0), 0);
    // Round total to nearest whole euro (100 cents)
    return Math.round(total / 100) * 100;
  }

  getItemCosts() {
    const selectedProducts = this._getSelectedProductsSync();
    if (!Array.isArray(selectedProducts)) return [];
    const finalPrices = this._calculateFinalPrices(selectedProducts);
    if (!Array.isArray(finalPrices)) return [];
    
    return finalPrices.map((product) => ({
      id: product.id,
      quantity: product.quantity || 0,
      unitPrice: product.originalUnitPrice || 0,
      sellingPlanPrice: product.sellingPlanDiscountedPrice || product.originalUnitPrice || 0,
      finalUnitPrice: product.finalUnitPrice || 0,
      totalPrice: product.finalTotalPrice || 0,
      bundleDiscountPercent: product.bundleDiscountPercent || 0,
      unitPriceFormatted: this._currencyFormatter.format((product.originalUnitPrice || 0) / 100),
      sellingPlanPriceFormatted: this._currencyFormatter.format((product.sellingPlanDiscountedPrice || product.originalUnitPrice || 0) / 100),
      finalUnitPriceFormatted: this._currencyFormatter.format((product.finalUnitPrice || 0) / 100),
      totalPriceFormatted: this._currencyFormatter.format((product.finalTotalPrice || 0) / 100),
    }));
  }

  getAveragePricePerItem() {
    const selectedProducts = this._getSelectedProductsSync();
    if (!Array.isArray(selectedProducts) || selectedProducts.length === 0) return 0;
    const totalPrice = this.getTotalPrice();
    const totalQuantity = this.getTotalQuantity();
    return totalQuantity > 0 ? totalPrice / totalQuantity : 0;
  }

  getAveragePricePerProduct() {
    const selectedProducts = this._getSelectedProductsSync();
    if (!Array.isArray(selectedProducts) || selectedProducts.length === 0) return 0;
    const totalPrice = this.getTotalPrice();
    return totalPrice / selectedProducts.length;
  }

  getTotals() {
    const selectedProducts = this._getSelectedProductsSync();
    if (!Array.isArray(selectedProducts)) {
      return {
        quantity: 0,
        price: 0,
        originalPrice: 0,
        savings: 0,
        bundleDiscountPercent: 0,
        selectedCount: 0,
        averagePricePerItem: 0,
        averagePricePerProduct: 0,
        items: [],
      };
    }
    
    const totalPrice = this.getTotalPrice();
    const totalQuantity = this.getTotalQuantity();
    const bundleDiscountPercent = this._getBundleDiscountPercentage(totalQuantity);
    
    // Calculate original total (before discounts)
    const originalTotal = selectedProducts.reduce((sum, product) => {
      return sum + (product.quantity || 0) * (product.price || 0);
    }, 0);
    
    // Calculate savings
    const savings = originalTotal - totalPrice;
    
    return {
      quantity: totalQuantity,
      price: totalPrice,
      originalPrice: originalTotal,
      savings: savings,
      bundleDiscountPercent: bundleDiscountPercent,
      selectedCount: selectedProducts.length,
      averagePricePerItem: totalQuantity > 0 ? totalPrice / totalQuantity : 0,
      averagePricePerProduct: selectedProducts.length > 0 ? totalPrice / selectedProducts.length : 0,
      items: this.getItemCosts(),
    };
  }

  _updateTotalData() {
    const totals = this.getTotals();
    const itemCosts = totals.items;

    // Update total quantity
    const qtyContainers = this.querySelectorAll('[data-total-qty]');
    if (qtyContainers) {
      qtyContainers.forEach((container) => {
        container.textContent = totals.quantity;
      });
    }

    // Update total price - search in form and also check for bundle-buy-buttons outside
    let priceContainers = this.querySelectorAll('[data-total-price]');
    if (priceContainers.length === 0) {
      // Try to find bundle-buy-buttons that reference this form
      const formId = this.id;
      if (formId) {
        const buyButtons = document.querySelector(`bundle-buy-buttons[form="${formId}"]`);
        if (buyButtons) {
          priceContainers = buyButtons.querySelectorAll('[data-total-price]');
        }
      }
      // Also try document-wide search as fallback
      if (priceContainers.length === 0) {
        priceContainers = document.querySelectorAll('[data-total-price]');
      }
    }
    
    if (priceContainers && priceContainers.length > 0) {
      priceContainers.forEach((container) => {
        const formattedPrice = this._currencyFormatter.format(totals.price / 100);
        container.textContent = formattedPrice;
      });
    }

    // Helper function to calculate price for a variant with specific discount scenario
    const calculatePriceForVariant = (variantId, useSellingPlan, basePrice, specificSellingPlanId = null) => {
      let discountedPrice = basePrice;
      
      // Apply selling plan discount if requested
      if (useSellingPlan) {
        const sellingPlanId = specificSellingPlanId || this._getSelectedSellingPlanId();
        
        if (sellingPlanId) {
          // First try to get discount from selling plan definition
          const discountPercent = this._getSellingPlanDiscountPercent(sellingPlanId);
          
          if (discountPercent > 0) {
            const discountMultiplier = (100 - discountPercent) / 100;
            discountedPrice = Math.round(basePrice * discountMultiplier);
          } else {
            // Fallback: try cache
            const cacheKey = `${variantId}-${sellingPlanId}`;
            const allocation = this._variantDataCache?.get(cacheKey);
            
            if (allocation?.price) {
              discountedPrice = allocation.price;
            }
          }
        }
      }
      
      // Apply bundle discount
      const totalQuantity = totals.quantity;
      const bundleDiscountPercent = this._getBundleDiscountPercentage(totalQuantity);
      const bundleDiscountMultiplier = (100 - bundleDiscountPercent) / 100;
      let finalPrice = Math.round(discountedPrice * bundleDiscountMultiplier);
      // Round to nearest whole euro (100 cents)
      finalPrice = Math.round(finalPrice / 100) * 100;
      
      return finalPrice;
    };

    // Get reference variant (first selected item or main product variant)
    const getReferenceVariant = () => {
      if (itemCosts.length > 0) {
        return {
          id: itemCosts[0].id,
          basePrice: itemCosts[0].unitPrice,
        };
      }
      // Fallback: get first variant from form
      const firstCheckbox = this.querySelector('input[name="id"]');
      if (firstCheckbox && firstCheckbox.hasAttribute('data-price')) {
        return {
          id: parseInt(firstCheckbox.value, 10),
          basePrice: parseFloat(firstCheckbox.getAttribute('data-price')) || 0,
        };
      }
      return null;
    };

    // Update unit prices for individual items (show final discounted price)
    const unitPriceElements = this.querySelectorAll('[data-unit-price]');
    if (unitPriceElements.length > 0) {
      unitPriceElements.forEach((element) => {
        // Try to find the variant ID from the element or its parent
        const variantId = this._getVariantIdFromElement(element);
        if (variantId) {
          // Find the item cost for this variant
          const itemCost = itemCosts.find((item) => item.id === variantId);
          if (itemCost) {
            // Show final discounted unit price (respects current selling plan selection)
            element.textContent = itemCost.finalUnitPriceFormatted;
          } else {
            // If not selected, calculate what the price would be with discounts
            const checkbox = this.querySelector(`input[name="id"][value="${variantId}"]`);
            if (checkbox && checkbox.hasAttribute('data-price')) {
              const basePrice = parseFloat(checkbox.getAttribute('data-price')) || 0;
              const sellingPlanId = this._getSelectedSellingPlanId();
              const finalPrice = calculatePriceForVariant(variantId, !!sellingPlanId, basePrice);
              element.textContent = this._currencyFormatter.format(finalPrice / 100);
            }
          }
        } else {
          // If no variant ID found, use reference variant
          const refVariant = getReferenceVariant();
          if (refVariant) {
            const sellingPlanId = this._getSelectedSellingPlanId();
            const finalPrice = calculatePriceForVariant(refVariant.id, !!sellingPlanId, refVariant.basePrice);
            element.textContent = this._currencyFormatter.format(finalPrice / 100);
          }
        }
      });
    }

    // Update one-time unit prices (no selling plan discount, but with bundle discount)
    const oneTimeUnitPriceElements = this.querySelectorAll('[data-one-time-unit-price]');
    if (oneTimeUnitPriceElements.length > 0) {
      const refVariant = getReferenceVariant();
      if (refVariant) {
        // Calculate one-time price: base price with bundle discount only (no selling plan discount)
        const totalQuantity = totals.quantity;
        const bundleDiscountPercent = this._getBundleDiscountPercentage(totalQuantity);
        const bundleDiscountMultiplier = (100 - bundleDiscountPercent) / 100;
        let finalPrice = Math.round(refVariant.basePrice * bundleDiscountMultiplier);
        // Round to nearest whole euro (100 cents)
        finalPrice = Math.round(finalPrice / 100) * 100;
        
        oneTimeUnitPriceElements.forEach((element) => {
          element.textContent = this._currencyFormatter.format(finalPrice / 100);
        });
      }
    }

    // Update subscription unit prices (with selling plan discount + bundle discount)
    const subscriptionUnitPriceElements = this.querySelectorAll('[data-subscription-unit-price]');
    if (subscriptionUnitPriceElements.length > 0) {
      const refVariant = getReferenceVariant();
      if (refVariant) {
        // Get the first available selling plan ID (not the selected one, but any available)
        const sellingPlanInput = this.querySelector('input[name="selling_plan"]:not([value=""])');
        const sellingPlanId = sellingPlanInput?.value || null;
        
        if (sellingPlanId) {
          // Use the specific selling plan ID for subscription price calculation
          const finalPrice = calculatePriceForVariant(refVariant.id, true, refVariant.basePrice, sellingPlanId);
          subscriptionUnitPriceElements.forEach((element) => {
            element.textContent = this._currencyFormatter.format(finalPrice / 100);
          });
        } else {
          // If no selling plan available, show one-time price as fallback
          const finalPrice = calculatePriceForVariant(refVariant.id, false, refVariant.basePrice);
          subscriptionUnitPriceElements.forEach((element) => {
            element.textContent = this._currencyFormatter.format(finalPrice / 100);
          });
        }
      }
    }
  }

  _getVariantIdFromElement(element) {
    // Check if element has data-variant-id attribute
    if (element.hasAttribute('data-variant-id')) {
      const variantId = parseInt(element.getAttribute('data-variant-id'), 10);
      if (!isNaN(variantId)) return variantId;
    }

    // Check if element is within a bundle-variant-card with a checkbox
    const variantCard = element.closest('bundle-variant-card');
    if (variantCard) {
      const checkbox = variantCard.querySelector('input[name="id"]');
      if (checkbox) {
        const variantId = parseInt(checkbox.value, 10);
        if (!isNaN(variantId)) return variantId;
      }
    }

    // Check if element is within the form and can find a nearby checkbox
    const form = element.closest('form');
    if (form) {
      // Try to find the closest checkbox input with name="id"
      const closestCheckbox = element.closest('li, .bundle-variant-card, label')?.querySelector('input[name="id"]');
      if (closestCheckbox) {
        const variantId = parseInt(closestCheckbox.value, 10);
        if (!isNaN(variantId)) return variantId;
      }
    }

    return null;
  }
};

if (!window.customElements.get('product-form-bundle')) {
  window.customElements.define('product-form-bundle', ProductFormBundle, {
    extends: 'form',
  });
}
